/**
 * CommunityCodesPanel — S1/S7. NECS Admin creates, regenerates and
 * deactivates the per-property community codes residents use to register.
 */
import { useCallback, useEffect, useState } from 'react';
import { KeyRound, RefreshCw, Copy, Megaphone } from 'lucide-react';
import { apiFetch } from '../../lib/apiFetch';
import { useAdminScopeTerms } from '../../lib/adminScopeTerms';
import { toast } from 'sonner';

const GOLD = '#C9A962';

export default function CommunityCodesPanel() {
  const terms = useAdminScopeTerms();
  const [data, setData] = useState(null);
  const [selectedProp, setSelectedProp] = useState('');
  const [ann, setAnn] = useState({ open: false, property_id: '', title: '', message: '', category: 'announcement' });

  const load = useCallback(async () => {
    try { setData(await apiFetch('/api/resident/admin/community-codes')); }
    catch (_) { setData({ codes: [], properties: [], resident_counts: {} }); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async (propertyId) => {
    if (!propertyId) { toast.error('Select a property'); return; }
    try {
      const res = await apiFetch('/api/resident/admin/community-codes', { method: 'POST', body: { property_id: propertyId } });
      toast.success(`Code ${res.code.code} generated`);
      load();
    } catch (e) { toast.error(e.detail || 'Generate failed'); }
  };

  const toggle = async (c) => {
    await apiFetch(`/api/resident/admin/community-codes/${c.code_id}`, { method: 'PATCH', body: { active: !c.active } });
    toast.success(c.active ? 'Code deactivated' : 'Code reactivated');
    load();
  };

  const sendAnn = async (e) => {
    e.preventDefault();
    try {
      const res = await apiFetch('/api/resident/admin/announcements', { method: 'POST', body: {
        property_id: ann.property_id, title: ann.title, message: ann.message, category: ann.category } });
      toast.success(`Announcement sent to ${res.sent_to} resident(s)`);
      setAnn({ open: false, property_id: '', title: '', message: '', category: 'announcement' });
    } catch (err) { toast.error(err.detail || 'Send failed'); }
  };

  if (!data) return null;
  const inputCls = 'max-w-full min-w-0 rounded bg-zinc-800 border border-zinc-600 px-2 py-1.5 text-white text-xs';

  return (
    <section className="rounded-xl border border-zinc-700 bg-zinc-900/70 p-5 mt-6" data-testid="community-codes-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h2 className="flex items-center gap-2 text-sm font-black uppercase tracking-[0.2em]" style={{ color: GOLD }}>
          <KeyRound className="h-4 w-4 shrink-0" /> {terms.occupant} Community Codes
        </h2>
        <div className="flex flex-wrap items-center gap-2 min-w-0 max-w-full">
          <select value={selectedProp} onChange={(e) => setSelectedProp(e.target.value)} className={inputCls} data-testid="cc-property-select">
            <option value="">Select property…</option>
            {data.properties.map((p) => <option key={p.property_id} value={p.property_id}>{p.property_name}</option>)}
          </select>
          <button onClick={() => generate(selectedProp)}
            className="px-3 py-1.5 rounded-full text-xs font-bold" style={{ backgroundColor: GOLD, color: '#1a1a1a' }} data-testid="cc-generate-btn">
            Generate / Regenerate
          </button>
          <button onClick={() => setAnn((s) => ({ ...s, open: !s.open, property_id: selectedProp || s.property_id }))}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold border border-zinc-600 text-zinc-300" data-testid="cc-announcement-toggle">
            <Megaphone className="h-3.5 w-3.5" /> Announcement
          </button>
          <button onClick={load} className="p-1.5 rounded-full border border-zinc-600 text-zinc-300" data-testid="cc-refresh-btn"><RefreshCw className="h-3.5 w-3.5" /></button>
        </div>
      </div>

      {ann.open && (
        <form onSubmit={sendAnn} className="rounded-lg border border-zinc-700 bg-zinc-800/40 p-3 mb-3 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="cc-announcement-form">
          <select required value={ann.property_id} onChange={(e) => setAnn((s) => ({ ...s, property_id: e.target.value }))} className={inputCls} data-testid="cc-ann-property">
            <option value="">Property…</option>
            {data.properties.map((p) => <option key={p.property_id} value={p.property_id}>{p.property_name}</option>)}
          </select>
          <select value={ann.category} onChange={(e) => setAnn((s) => ({ ...s, category: e.target.value }))} className={inputCls} data-testid="cc-ann-category">
            <option value="announcement">General Announcement</option>
            <option value="service_scheduled">Service Scheduled</option>
            <option value="service_completed">Service Completed</option>
            <option value="service_delayed">Service Delayed / Rescheduled</option>
          </select>
          <input required minLength={3} placeholder="Title" value={ann.title} onChange={(e) => setAnn((s) => ({ ...s, title: e.target.value }))} className={`${inputCls} sm:col-span-2`} data-testid="cc-ann-title" />
          <textarea required minLength={3} placeholder={`Message to all ${terms.occupants.toLowerCase()} of this community…`} value={ann.message} onChange={(e) => setAnn((s) => ({ ...s, message: e.target.value }))} className={`${inputCls} sm:col-span-2 min-h-[60px]`} data-testid="cc-ann-message" />
          <button type="submit" className="px-4 py-1.5 rounded-full text-xs font-bold w-fit" style={{ backgroundColor: GOLD, color: '#1a1a1a' }} data-testid="cc-ann-send-btn">
            Send to Community
          </button>
        </form>
      )}

      {data.codes.length === 0 ? (
        <p className="text-xs text-zinc-500" data-testid="cc-empty">No community codes yet — select a property to open {terms.occupant.toLowerCase()} registration.</p>
      ) : (
        <div className="space-y-1.5">
          {data.codes.map((c) => (
            <div key={c.code_id} className={`flex items-center justify-between gap-2 rounded-lg border p-2.5 ${c.active ? 'border-zinc-700 bg-zinc-800/40' : 'border-zinc-800 bg-zinc-900/40 opacity-60'}`}
              data-testid={`cc-row-${c.code_id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <button onClick={() => { navigator.clipboard?.writeText(c.code); toast.success('Copied'); }}
                  className="flex items-center gap-1.5 font-mono font-bold text-sm shrink-0" style={{ color: GOLD }} data-testid={`cc-code-${c.code_id}`}>
                  {c.code} <Copy className="h-3 w-3 opacity-60" />
                </button>
                <span className="text-xs text-zinc-300 truncate">{c.property_name}</span>
                <span className="text-[10px] text-zinc-500 shrink-0">{data.resident_counts?.[c.property_id] ?? 0} residents</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {!c.active && <span className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-500 text-[9px] font-black uppercase">Inactive</span>}
                <button onClick={() => toggle(c)}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${c.active ? 'border-red-800 text-red-400' : 'border-emerald-800 text-emerald-400'}`}
                  data-testid={`cc-toggle-${c.code_id}`}>
                  {c.active ? 'Deactivate' : 'Reactivate'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="text-[10px] text-zinc-600 mt-2.5">Residents self-register in the NECS Resident app at <span style={{ color: GOLD }}>/resident</span> using their community code. Regenerating deactivates the old code instantly.</p>
    </section>
  );
}
